import { useMemo } from 'react';
import { FieldConfig, ConditionalField } from '../types';

/**
 * Hook to handle conditional field visibility
 * Shows/hides fields based on radio or checkbox values
 * Includes circular reference prevention and depth limiting
 */
export const useConditionalFields = (
  fields: FieldConfig[],
  formValues: Record<string, any>,
  maxDepth: number = 5
): FieldConfig[] => {
  const allFields = useMemo(() => {
    const result: FieldConfig[] = [];
    const processedFields = new Set<string>(); // Prevent circular references

    const processFieldsRecursively = (
      fieldsToProcess: FieldConfig[],
      depth: number = 0
    ) => {
      // Prevent infinite recursion
      if (depth > maxDepth) {
        console.warn(
          `useConditionalFields: Maximum depth (${maxDepth}) reached. Possible circular reference.`
        );
        return;
      }

      fieldsToProcess.forEach((field) => {
        // Prevent processing the same field multiple times
        const fieldKey = `${field.name}_${depth}`;
        if (processedFields.has(fieldKey)) {
          return;
        }
        processedFields.add(fieldKey);

        // Add the main field
        result.push(field);

        // Check if this field has conditional fields
        if (field.conditionalFields && field.conditionalFields.length > 0) {
          const fieldValue = formValues[field.name];

          field.conditionalFields.forEach((conditionalConfig: ConditionalField) => {
            let shouldShow = false;

            // Check condition
            if (conditionalConfig.condition) {
              shouldShow = conditionalConfig.condition(fieldValue, formValues);
            } else if (conditionalConfig.is !== undefined) {
              // Handle array of values
              if (Array.isArray(conditionalConfig.is)) {
                shouldShow = conditionalConfig.is.includes(fieldValue);
              } else {
                shouldShow = fieldValue === conditionalConfig.is;
              }
            }

            // Recursively process conditional fields if condition is met
            if (shouldShow && conditionalConfig.fields) {
              processFieldsRecursively(conditionalConfig.fields, depth + 1);
            }
          });
        }
      });
    };

    processFieldsRecursively(fields);

    return result;
  }, [fields, formValues, maxDepth]);

  return allFields;
};
